import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import client, { endpoints, errText } from '../../api/client';
import StatusBadge from '../../components/StatusBadge';
import { money, FREQUENCY_LABEL } from '../../lib/format';

const STATUSES = ['awaiting_deposit', 'confirmed', 'assigned', 'in_progress', 'quality_check', 'completed', 'cancelled'];

// Filters live in the URL, not in component state, so a filtered view can be
// bookmarked and the board's queue links land on exactly the right list.
export default function Bookings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const status = params.get('status') || '';
  const q = params.get('q') || '';
  const unassigned = params.get('unassigned') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await client.get(endpoints.bookings.list, {
        params: { status: status || undefined, q: q || undefined, unassigned: unassigned || undefined, from: from || undefined, to: to || undefined, limit: 100 },
      });
      setData(r.data);
    } catch (e) { setErr(errText(e)); }
    setLoading(false);
  }, [status, q, unassigned, from, to]);

  useEffect(() => { load(); }, [load]);

  const set = (key) => (e) => {
    const next = new URLSearchParams(params);
    if (e.target.value) next.set(key, e.target.value); else next.delete(key);
    setParams(next);
  };

  return (
    <section className="section">
      <div className="section-head">
        <h1>Bookings <span className="muted">({data.total})</span></h1>
      </div>

      <div className="filters">
        <label>Status
          <select value={status} onChange={set('status')}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
        <label>From<input type="date" value={from} onChange={set('from')} /></label>
        <label>To<input type="date" value={to} onChange={set('to')} /></label>
        <label>Search
          <input placeholder="reference, name, phone, address" defaultValue={q}
                 onKeyDown={(e) => { if (e.key === 'Enter') set('q')(e); }} />
        </label>
        <label className="check">
          <input type="checkbox" checked={unassigned === '1'}
                 onChange={(e) => {
                   const next = new URLSearchParams(params);
                   if (e.target.checked) next.set('unassigned', '1'); else next.delete('unassigned');
                   setParams(next);
                 }} />
          Unassigned only
        </label>
      </div>

      {err && <p className="alert alert-err">{err}</p>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && data.items.length === 0 && <p className="muted">No bookings match those filters.</p>}

      {data.items.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>When</th><th>Reference</th><th>Service</th><th>Customer</th><th>City</th><th>Team</th><th>Status</th><th>Deposit</th><th>Balance</th></tr>
          </thead>
          <tbody>
            {data.items.map((b) => (
              <tr key={b._id} className={b.status === 'cancelled' ? 'row-muted' : ''}>
                <td>{b.date}<div className="muted small">{b.time}</div></td>
                <td><Link to={`/ops/bookings/${b.reference}`}>{b.reference}</Link></td>
                <td>{b.serviceName}<div className="muted small">{FREQUENCY_LABEL[b.frequency] || b.frequency}{b.channel === 'b2b' ? ' · B2B' : ''}</div></td>
                <td>{b.contact?.name}<div className="muted small">{b.contact?.phone}</div></td>
                <td>{b.city}</td>
                <td>{b.team?.name || <span className="muted">—</span>}</td>
                <td><StatusBadge status={b.status} /></td>
                <td className={b.money?.depositPaid ? 'paid' : 'due'}>
                  {money(b.money?.depositMinor, b.money?.currency)}
                  <div className="muted small">{b.money?.depositPaid ? 'paid' : 'due'}</div>
                </td>
                <td className={b.money?.balancePaid ? 'paid' : 'due'}>
                  {money(b.money?.balanceMinor, b.money?.currency)}
                  <div className="muted small">{b.money?.balancePaid ? 'paid' : 'due'}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
